/**
 * Web-push helpers used by the schedule page and AlertsManager.
 *
 * We do NOT use per-user auth here — the browser endpoint itself IS the
 * unique identifier, so we send the current subscription's endpoint along
 * with the list of showIds the user is subscribed to. The backend keeps a
 * push_subscriptions collection and fires 5-min alerts server-side.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "gfn_show_alerts";

function base64UrlToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function currentPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

async function fetchVapidPublicKey() {
  const res = await fetch(`${BACKEND}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error("Failed to fetch VAPID key");
  const { public_key } = await res.json();
  if (!public_key) throw new Error("Push not configured on server");
  return public_key;
}

export function getSubscribedShowIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

export function setSubscribedShowIds(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {}
}

async function ensureServiceWorkerReady() {
  if (!("serviceWorker" in navigator)) throw new Error("Service worker not supported");
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function ensurePushSubscription() {
  if (!isPushSupported()) throw new Error("Push not supported by this browser");
  const perm = currentPermission();
  if (perm === "denied") throw new Error("Notifications are blocked in browser settings");
  if (perm !== "granted") {
    const requested = await Notification.requestPermission();
    if (requested !== "granted") throw new Error("Permission not granted");
  }
  const reg = await ensureServiceWorkerReady();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const key = await fetchVapidPublicKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(key),
    });
  }
  return sub;
}

export async function syncSubscribedShows() {
  if (!isPushSupported()) return;
  const reg = await ensureServiceWorkerReady();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return; // no push subscription yet — nothing to sync
  const shows = Array.from(getSubscribedShowIds());
  await fetch(`${BACKEND}/api/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      endpoint: sub.endpoint,
      keys: sub.toJSON().keys,
      subscribed_shows: shows,
    }),
  });
}

export function toggleShowSubscription(showId) {
  const set = getSubscribedShowIds();
  if (set.has(showId)) set.delete(showId);
  else set.add(showId);
  setSubscribedShowIds(set);
  return set;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const reg = await ensureServiceWorkerReady();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await fetch(`${BACKEND}/api/push/unsubscribe`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
  } catch {}
  try {
    await sub.unsubscribe();
  } catch {}
}

export const ALERT_LEAD_MINUTES = 5;
